import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaWhatsapp, FaSignOutAlt } from "react-icons/fa";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

const APP_ID = "127023";

const VOLATILITIES = [
  { name: "Volatility 10 (1s) Index", symbol: "1HZ10V" },
  { name: "Volatility 10 Index", symbol: "R_10" },
  { name: "Volatility 15 (1s) Index", symbol: "1HZ15V" },
  { name: "Volatility 25 (1s) Index", symbol: "1HZ25V" },
  { name: "Volatility 25 Index", symbol: "R_25" },
  { name: "Volatility 30 (1s) Index", symbol: "1HZ30V" },
  { name: "Volatility 50 (1s) Index", symbol: "1HZ50V" },
  { name: "Volatility 50 Index", symbol: "R_50" },
  { name: "Volatility 75 (1s) Index", symbol: "1HZ75V" },
  { name: "Volatility 75 Index", symbol: "R_75" },
  { name: "Volatility 90 (1s) Index", symbol: "1HZ90V" },
  { name: "Volatility 100 (1s) Index", symbol: "1HZ100V" },
  { name: "Volatility 100 Index", symbol: "R_100" },
];

const scaleConfidence = (raw) => Math.round(85 + (raw / 100) * 14);

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ticks, setTicks] = useState({});
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);

  const ticksRef = useRef({});

  useEffect(() => {
    ticksRef.current = ticks;
  }, [ticks]);

  // Handle OAuth uid
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlUid = params.get("uid");
    if (urlUid) {
      localStorage.setItem("userUid", urlUid);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      const loginid = localStorage.getItem('currentDerivLogin');
      if (!loginid) {
        navigate("/login");
        return;
      }

      try {
        const usersCol = collection(db, "deriv_users");
        const q = query(usersCol, where("loginid", "==", loginid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          localStorage.removeItem('currentDerivLogin');
          navigate("/login");
          return;
        }

        const userDoc = querySnapshot.docs[0];
        const data = userDoc.data();
        const { approved } = data;

        if (approved) {
          setUser(data);
        } else {
          navigate("/pending");
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        localStorage.removeItem('currentDerivLogin');
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate]);

  // WebSockets
  useEffect(() => {
    const sockets = VOLATILITIES.map(({ symbol }) => {
      const ws = new WebSocket(`wss://ws.binaryws.com/websockets/v3?app_id=${APP_ID}`);
      ws.onopen = () => ws.send(JSON.stringify({ ticks: symbol, subscribe: 1 }));
      ws.onmessage = (e) => {
        const res = JSON.parse(e.data);
        if (res.tick?.quote !== undefined) {
          const digit = Math.floor(res.tick.quote * 10) % 10;
          setTicks((prev) => ({
            ...prev,
            [symbol]: [...(prev[symbol] || []), digit].slice(-50),
          }));
        }
      };
      return ws;
    });

    return () => sockets.forEach((ws) => ws.close());
  }, []);

  // Timer + force signal generation after every countdown
  useEffect(() => {
    const generateAllSignals = () => {
      VOLATILITIES.forEach(({ symbol }) => {
        const history = ticksRef.current[symbol];
        if (!history || history.length < 10) return;

        const recent = history.slice(-50);
        const freq = Array(10).fill(0);
        recent.forEach((d) => freq[d]++);

        let maxCount = 0;
        let bestDigit = 0;
        freq.forEach((count, digit) => {
          if (count > maxCount) {
            maxCount = count;
            bestDigit = digit;
          }
        });

        const dominance = maxCount / recent.length;
        const confidence = scaleConfidence(dominance * 100);
        const evenOdd = bestDigit % 2 === 0 ? "EVEN" : "ODD";

        setSignals((prev) => ({
          ...prev,
          [symbol]: { digit: bestDigit, confidence, evenOdd },
        }));
      });
    };

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (phase === "countdown") {
            setPhase("entry");
            generateAllSignals(); // Force prediction right after countdown ends
            return 5;
          }
          if (phase === "entry") {
            setPhase("trade");
            return 5;
          }
          if (phase === "trade") {
            setPhase("countdown");
            return 15;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  const handleLogout = () => {
    localStorage.removeItem("currentDerivLogin");
    navigate("/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center text-gray-400">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-600 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col">
      {/* Header */}
      <header className="bg-[#0a1325] border-b border-gray-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Logo" className="h-8 w-auto" />
            <h1 className="text-xl font-bold text-cyan-400">Blooper Matches</h1>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400 hidden sm:block">
              {user.name || user.email?.split("@")[0] || "User"}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded text-sm"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Catchy headline */}
      <div className="text-center py-6 px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-cyan-300">
          Unlock Rare Matches Windows
        </h2>
        <p className="text-gray-400 mt-1 text-base sm:text-lg">
          Real-time predictions • Beat the market noise
        </p>
      </div>

      {/* Cards Grid – 1 per row on mobile */}
      <main className="flex-1 px-4 sm:px-6 pb-12 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {VOLATILITIES.map(({ name, symbol }) => {
            const signal = signals[symbol] || {};
            const showEntry = phase === "entry" && signal.digit !== undefined;
            const showTrade = phase === "trade" && signal.digit !== undefined;

            return (
              <div
                key={symbol}
                className="bg-[#0a1325]/80 border border-cyan-900/50 rounded-2xl p-4 flex flex-col items-center text-center"
              >
                <h3 className="text-base font-semibold text-cyan-300 mb-3 w-full text-center">
                  {name}
                </h3>

                {showEntry || showTrade ? (
                  <>
                    <div
                      className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center text-3xl md:text-4xl font-extrabold mb-2 ${
                        signal.digit % 2 === 0
                          ? "bg-gradient-to-tr from-cyan-500 to-blue-500"
                          : "bg-gradient-to-tr from-indigo-600 to-purple-500"
                      }`}
                    >
                      {signal.digit}
                    </div>

                    <p className="text-yellow-300 text-base font-semibold mb-1">
                      {signal.confidence}%
                    </p>

                    <p className="text-sm text-gray-300 mb-1">
                      {signal.evenOdd}
                    </p>

                    {showEntry ? (
                      <p className="text-base text-cyan-300 font-medium">
                        ENTRY in {timer}s
                      </p>
                    ) : (
                      <p className="text-xl text-green-400 font-bold">
                        TRADE
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#050814] border border-cyan-900/40 flex items-center justify-center text-3xl md:text-4xl font-bold text-gray-400 mb-2">
                      {timer}s
                    </div>
                    <p className="text-sm text-gray-400">Next prediction</p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-sm text-gray-500">
          Need help?{" "}
          <a
            href="https://wa.me/254716691630"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400 hover:underline inline-flex items-center gap-1"
          >
            <FaWhatsapp /> 0716691630
          </a>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-gray-600 border-t border-gray-800 mt-auto">
        © 2026 Matches Blooper AI • Trading involves high risk of loss
      </footer>
    </div>
  );
}